import type { ReactNode } from "react";
import { useLocale } from "@hooks/useLocale";
import { QueryState } from "@components/ui/QueryState";
import { StatBand, type Stat } from "@components/ui/StatBand";
import { useGetEventSafetyQuery, useGetReportSafetyQuery } from "@api/pApi";
import { fmtNum } from "@lib/format";
import type { SafetyDayOut } from "@api/types";

/**
 * HSE panel — the event-level LTI rollup (StatBand + daily HSE trail) plus the
 * selected report's daily HSE header. Safety is a DAILY HEADER (no discrete
 * open→close action list). The FE follows the live Oxbo-P `SafetyOut` contract:
 * LTI-free days, compliance-check counts (RID cards / JSA / jobsite eval),
 * well-control pressures, and company/government inspection flags.
 */
export function HsePanel({ eventId, reportId }: { eventId: string; reportId: string }) {
  const { t, locale } = useLocale();

  const {
    data: rollup,
    isLoading: rollupLoading,
    isError: rollupError,
    refetch,
  } = useGetEventSafetyQuery(eventId, { skip: !eventId });
  const { data: day } = useGetReportSafetyQuery(reportId, { skip: !reportId });

  // Nullable-number formatter — a missing counter shows a muted dash.
  const num = (n: number | null | undefined) => (n == null ? "—" : fmtNum(n, locale));

  const items = Array.isArray(rollup?.items) ? rollup.items : [];

  // The live HSE header is sparse — most compliance/pressure fields land NULL on a
  // given day. Count the days that actually carry a compliance value so the trail
  // can say "recorded on N of M days" instead of implying every day was logged.
  const COMPLIANCE_FIELDS = [
    "rid_cards",
    "job_safety_analysis",
    "jobsite_evaluation",
    "is_company_inspected",
    "is_government_inspected",
  ] as const;
  const recordedDays = items.filter((it) =>
    COMPLIANCE_FIELDS.some((f) => it[f] != null),
  ).length;

  // LTI counters are themselves frequently null on this dataset — only surface the
  // days-since-LTI stats when at least one is present (an all-dash band is noise).
  const hasLti =
    rollup != null &&
    (rollup.days_since_lti_total != null || rollup.days_since_lti_operator != null);

  const stats: Stat[] = rollup
    ? [
        ...(hasLti
          ? ([
              {
                label: t("hse.kpi.daysSinceLti"),
                value: num(rollup.days_since_lti_total),
                tone: "good",
              },
              {
                label: t("hse.kpi.daysSinceLtiOperator"),
                value: num(rollup.days_since_lti_operator),
                tone: "good",
              },
            ] as Stat[])
          : []),
        { label: t("hse.kpi.reportDays"), value: fmtNum(items.length, locale), tone: "ops" },
      ]
    : [];

  return (
    <QueryState
      isLoading={rollupLoading}
      isError={rollupError}
      isEmpty={!rollup}
      emptyMessage={t("hse.noData")}
      onRetry={refetch}
    >
      {stats.length ? <StatBand stats={stats} /> : null}

      {day ? <DayHeader day={day} /> : null}

      {/* daily HSE trail — one row per report day (compliance checks + inspection) */}
      <div className="card" style={{ marginTop: 14 }}>
        <h3>
          {t("hse.trail.title")}
          {items.length > 0 ? (
            <span className="dim" style={{ fontWeight: 400, fontSize: 12 }}>
              {" "}
              — {t("hse.trail.recorded", { n: recordedDays, m: items.length })}
            </span>
          ) : null}
        </h3>
        <div className="table-scroll">
          <table>
            <thead>
              <tr>
                <th>{t("hse.trail.date")}</th>
                <th className="num">{t("hse.trail.ridCards")}</th>
                <th className="num">{t("hse.trail.jsa")}</th>
                <th className="num">{t("hse.trail.jobsiteEval")}</th>
                <th className="num">{t("hse.trail.bopAnnular")}</th>
                <th>{t("hse.trail.companyInsp")}</th>
                <th>{t("hse.trail.govtInsp")}</th>
              </tr>
            </thead>
            <tbody>
              {items.length === 0 ? (
                <tr>
                  <td colSpan={7} className="dim" style={{ textAlign: "center", padding: "12px 0" }}>
                    {t("hse.noTrail")}
                  </td>
                </tr>
              ) : (
                items.map((it) => (
                  <tr
                    key={it.id}
                    data-owner="p"
                    className={it.daily_report_id === reportId ? "row-selected" : undefined}
                  >
                    <td className="tab-nums">{it.date_report ?? "—"}</td>
                    <td className="num tab-nums">{num(it.rid_cards)}</td>
                    <td className="num tab-nums">{num(it.job_safety_analysis)}</td>
                    <td className="num tab-nums">{num(it.jobsite_evaluation)}</td>
                    <td className="num tab-nums">{num(it.bop_pressure_annular)}</td>
                    <td>
                      <YesNoBadge value={it.is_company_inspected} />
                    </td>
                    <td>
                      <YesNoBadge value={it.is_government_inspected} />
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </QueryState>
  );
}

/** The selected report's daily HSE header — compliance / well-control / inspection. */
function DayHeader({ day }: { day: SafetyDayOut }) {
  const { t, locale } = useLocale();
  const num = (n: number | null | undefined) => (n == null ? t("common.na") : fmtNum(n, locale));

  return (
    <div className="card" style={{ marginTop: 14 }}>
      <h3>
        {t("hse.day.title")} <span className="tag-op">{day.date_report ?? "—"}</span>
      </h3>

      <h4 className="hse-subhead">{t("hse.day.checks")}</h4>
      <div className="kv-grid">
        <KvNum k={t("hse.day.ridCards")} v={num(day.rid_cards)} />
        <KvNum k={t("hse.day.jsa")} v={num(day.job_safety_analysis)} />
        <KvNum k={t("hse.day.jobsiteEval")} v={num(day.jobsite_evaluation)} />
      </div>

      {/* Well-control pressures are rarely logged — hide the whole group when the
          day carries neither, rather than showing two "N/A" rows. */}
      {day.bop_pressure_annular != null || day.last_casing_test_pressure != null ? (
        <>
          <h4 className="hse-subhead">{t("hse.day.wellControl")}</h4>
          <div className="kv-grid">
            <KvNum k={t("hse.day.bopAnnular")} v={num(day.bop_pressure_annular)} />
            <KvNum k={t("hse.day.casingTest")} v={num(day.last_casing_test_pressure)} />
          </div>
        </>
      ) : null}

      <h4 className="hse-subhead">{t("hse.day.inspections")}</h4>
      <div className="kv-grid">
        <Kv k={t("hse.day.companyInsp")}>
          <YesNoBadge value={day.is_company_inspected} />
        </Kv>
        <Kv k={t("hse.day.govtInsp")}>
          <YesNoBadge value={day.is_government_inspected} />
        </Kv>
      </div>
    </div>
  );
}

function Kv({ k, children }: { k: string; children: ReactNode }) {
  return (
    <div className="kv">
      <span className="kv-k">{k}</span>
      <span className="kv-v">{children}</span>
    </div>
  );
}

function KvNum({ k, v }: { k: string; v: string }) {
  return (
    <div className="kv">
      <span className="kv-k">{k}</span>
      <span className="kv-v mono tab-nums">{v}</span>
    </div>
  );
}

/** Yes / No / — badge for a nullable boolean inspection flag. */
function YesNoBadge({ value }: { value: boolean | null | undefined }) {
  const { t } = useLocale();
  if (value === true) return <span className="badge var-good">{t("hse.yes")}</span>;
  if (value === false) return <span className="badge">{t("hse.no")}</span>;
  return <span className="badge">{t("hse.bop.na")}</span>;
}
